import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useReveal } from "@/hooks/use-reveal";
import hero from "@/assets/dj-mariage-hero.jpg";
import reception from "@/assets/dj-mariage-reception.jpg";
import { PageHero } from "@/components/page-hero";
import { trackEvent, trackGalleryOpen } from "@/lib/analytics";
import { useSectionView } from "@/hooks/use-section-view";
import { metaDesc } from "@/lib/seo-description";
import { REALISATIONS, type Realisation } from "@/data/realisations";

import { ReviewsProof } from "@/components/reviews-proof";
const TITLE = "Réalisations DJ mariage Provence : galerie | DJ Val";
const DESC =
  "Galerie des réalisations DJ mariage en Provence et Vaucluse : domaines, bastides, châteaux. Filtrez par type d'événement et par lieu de réception.";

export const Route = createFileRoute("/galerie")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: metaDesc(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: metaDesc(DESC) },
      { property: "og:url", content: "https://dj-val.fr/galerie" },
      { property: "og:image", content: `https://dj-val.fr${reception}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${reception}` },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/galerie" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Réalisations DJ mariage Provence — DJ Val",
          description: DESC,
          url: "https://dj-val.fr/galerie",
        }),
      },
    ],
  }),
  component: Page,
});

const CATEGORIES = ["Tous", "Mariage", "Entreprise", "Anniversaire"] as const;
type Categorie = (typeof CATEGORIES)[number];

/** Optimise une image distante (WebP + largeur demandée) et prépare le srcset. */
function imageSources(src: string) {
  const isRemote = src.startsWith("http");
  if (!isRemote) return { src, srcSet: undefined as string | undefined };
  const base = src.split("?")[0];
  const widths = [480, 768, 1000, 1400];
  const url = (w: number) =>
    `${base}?fm=webp&auto=compress&fit=crop&w=${w}&q=72`;
  return {
    src: url(1000),
    srcSet: widths.map((w) => `${url(w)} ${w}w`).join(", "),
  };
}

function year(r: Realisation): string {
  return r.date.slice(0, 4);
}

function altText(r: Realisation): string {
  const lieu = r.venue ? `${r.venue}, ${r.city}` : r.city;
  return `${r.title} — ${r.category.toLowerCase()} animé par DJ Val à ${lieu} en ${year(r)}, piste de danse et éclairage`;
}

function Card({ r, index }: { r: Realisation; index: number }) {
  const { src, srcSet } = imageSources(r.image);
  const legende = (
    <>
      <p className="font-serif-display text-lg text-foreground leading-snug">
        {r.venue ?? r.title}
      </p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        {r.city} · {year(r)} · {r.guests} invités
      </p>
      <p className="mt-2 text-sm font-light leading-relaxed">{r.description}</p>
    </>
  );

  const media = (
    <div className="overflow-hidden border border-foreground/10 group">
      <img
        src={src}
        srcSet={srcSet}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={altText(r)}
        loading={index < 3 ? "eager" : "lazy"}
        decoding="async"
        width={1000}
        height={667}
        className="w-full h-64 object-cover transition-transform duration-[1200ms] group-hover:scale-105"
      />
    </div>
  );

  return (
    <article
      className="sr break-inside-avoid mb-8"
      data-reveal
      style={{ transitionDelay: `${(index % 6) * 60}ms` }}
    >
      {r.venueSlug ? (
        <Link
          to="/lieux/$slug"
          params={{ slug: r.venueSlug }}
          onClick={() => {
            trackGalleryOpen("galerie");
            trackEvent("gallery_venue_click", { section_name: r.venueSlug ?? "" });
          }}
          className="block"
          aria-label={`Découvrir le lieu ${r.venue}`}
        >
          {media}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => trackGalleryOpen("galerie")}
          className="block w-full text-left"
          aria-label={`Voir : ${r.title}`}
        >
          {media}
        </button>
      )}
      <figcaption className="mt-3">
        {r.venueSlug ? (
          <Link
            to="/lieux/$slug"
            params={{ slug: r.venueSlug }}
            className="hover:text-[var(--gold)] transition-colors block"
          >
            {legende}
          </Link>
        ) : (
          legende
        )}
      </figcaption>
    </article>
  );
}

function Page() {
  useReveal();
  const galleryRef = useSectionView<HTMLElement>("galerie");
  const ctaRef = useSectionView<HTMLElement>("galerie_cta_final");

  const [categorie, setCategorie] = useState<Categorie>("Tous");
  const [lieu, setLieu] = useState<string>("Tous les lieux");

  const lieux = useMemo(() => {
    const set = new Set<string>();
    REALISATIONS.forEach((r) => {
      if (r.venue) set.add(r.venue);
    });
    return ["Tous les lieux", ...Array.from(set).sort((a, b) => a.localeCompare(b, "fr"))];
  }, []);

  const items = useMemo(
    () =>
      REALISATIONS.filter(
        (r) =>
          (categorie === "Tous" || r.category === categorie) &&
          (lieu === "Tous les lieux" || r.venue === lieu),
      ).sort((a, b) => b.date.localeCompare(a.date)),
    [categorie, lieu],
  );

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={hero}
        eyebrow="Réalisations"
        title={<>Nos plus belles <span className="italic">soirées</span></>}
        subtitle="Mariages, soirées d'entreprise et anniversaires animés en Provence — domaine par domaine, année par année."
        primary={{ label: "Demander un devis", to: "/contactez-nous" }}
        height="compact"
      />
      <ReviewsProof />

      {/* ===== INTRODUCTION SEO (250 mots) ===== */}
      <section className="pt-16 pb-10 px-6 max-w-3xl mx-auto">
        <h2 className="font-serif-display text-3xl text-foreground">
          Réalisations DJ mariage en Provence
        </h2>
        <span className="gold-rule !w-12 my-5 block" />
        <div className="space-y-4 text-sm sm:text-base font-light leading-relaxed">
          <p>
          Cette galerie rassemble les réalisations DJ mariage en Provence signées DJ Val :
            plus de vingt ans de soirées dans les domaines du Luberon, les bastides du Vaucluse,
            les mas des Alpilles et les lieux de réception des Bouches-du-Rhône. Chaque visuel
            correspond à un événement réel, avec son domaine d'accueil, son année et le nombre
            d'invités présents ce soir-là. L'objectif n'est pas d'aligner de jolies images :
            c'est de vous montrer à quoi ressemble concrètement une piste de danse pleine à
            Robion, à Lioux, aux Baux-de-Provence ou à Caumont-sur-Durance.
          </p>
          <p>
            Vous pouvez filtrer les réalisations par type d'événement — mariage, soirée
            d'entreprise, anniversaire — et par lieu de réception. Si le domaine dispose d'une
            fiche détaillée sur le site, la photo est cliquable : vous accédez alors aux
            contraintes acoustiques du lieu, aux horaires de fin autorisés, aux accès techniques
            et à mes retours d'expérience sur place. C'est souvent l'information la plus utile
            quand vous hésitez encore entre deux domaines.
          </p>
          <p>
            Ce que vous retrouvez d'une réalisation à l'autre : une sonorisation calibrée pour la
            salle plutôt que pour le catalogue, une mise en lumière qui habille l'espace sans
            l'écraser, une cérémonie et un vin d'honneur sonorisés proprement, puis une soirée
            dansante construite avec vous en amont. Un seul événement par week-end, systématiquement,
            afin d'installer, régler et rester présent du début à la fin.
          </p>
          <p>
            Votre domaine ne figure pas encore ici ? Il y a de fortes chances que j'y sois déjà
            intervenu — demandez-moi simplement.
          </p>
        </div>
      </section>

      {/* ===== FILTRES ===== */}
      <section className="px-6 max-w-7xl mx-auto pb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-y border-foreground/10 py-5">
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Filtrer par type d'événement"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={categorie === c}
                onClick={() => {
                  setCategorie(c);
                  trackEvent("gallery_filter", { section_name: `categorie_${c}` });
                }}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  categorie === c
                    ? "border-[var(--gold)] text-[var(--gold)]"
                    : "border-foreground/15 hover:border-foreground/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <label
              htmlFor="filtre-lieu"
              className="text-[11px] uppercase tracking-[0.2em]"
            >
              Lieu de réception
            </label>
            <select
              id="filtre-lieu"
              value={lieu}
              onChange={(e) => {
                setLieu(e.target.value);
                trackEvent("gallery_filter", { section_name: `lieu_${e.target.value}` });
              }}
              className="bg-transparent border border-foreground/15 px-3 py-2 text-sm text-foreground focus:border-[var(--gold)] outline-none"
            >
              {lieux.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-3 text-[11px] uppercase tracking-[0.2em]" aria-live="polite">
          {items.length} réalisation{items.length > 1 ? "s" : ""} affichée
          {items.length > 1 ? "s" : ""}
        </p>
      </section>

      {/* ===== GRILLE ===== */}
      <section ref={galleryRef} className="pb-24 px-6 max-w-7xl mx-auto">
        {items.length === 0 ? (
          <p className="py-16 text-center text-sm">
            Aucune réalisation ne correspond à ce filtre — essayez une autre combinaison.
          </p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-8 [column-fill:_balance]">
            {items.map((r, i) => (
              <Card key={r.id} r={r} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ===== CTA FINAL ===== */}
      <section
        ref={ctaRef}
        className="py-20 px-6 bg-card border-y border-foreground/10 text-center"
      >
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] font-semibold">
            Votre devis en 24 h
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-3">
            Votre mariage sera le <span className="italic">prochain</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-6 block" />
          <p className="text-sm font-light leading-relaxed">
            Dites-moi la date, le domaine et le nombre d'invités : vous recevez une proposition
            personnalisée sous 24 h, gratuite et sans engagement.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contactez-nous"
              onClick={() =>
                trackEvent("cta_click", {
                  cta_label: "Votre devis en 24 h",
                  cta_location: "galerie_cta_final",
                })
              }
              className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Votre devis en 24 h
            </Link>
            <a
              href="tel:0767908667"
              data-cta-location="galerie_cta_final"
              className="inline-block border border-foreground/20 px-10 py-4 text-[13px] font-semibold tracking-[0.12em] text-foreground hover:border-[var(--gold)] transition-colors"
              style={{ fontFamily: "Arial, sans-serif", fontVariantNumeric: "tabular-nums" }}
            >
              07 67 90 86 67
            </a>
          </div>
          <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Réponse sous 24 h · Un seul événement par week-end · Devis gratuit et sans engagement
          </p>
        </div>
      </section>
    </div>
  );
}
